<template>
  <div
    class="tw:flex tw:flex-1"
    :class="{
      'tw:flex-col tw:overflow-auto': isMobile,
      'tw:overflow-hidden': !isMobile,
    }"
  >
    <StreamFiltersMediaSelector
      v-model:media="media"
      v-model:medium="selectedMedium"
      v-model:refresh-media="refreshMedia"
      :class="{ 'tw:w-[220px]': !isMobile }"
      data-testid="media-selector"
      :stream="stream"
      @toggle-medium="toggleMedium"
    />
    <div class="tw:w-px tw:bg-denim-200" />
    <div
      v-if="selectedMedium && media.includes(selectedMedium.medium)"
      class="tw:flex tw:flex-1 tw:flex-col"
      :class="{ 'tw:overflow-hidden': !isMobile }"
    >
      <div
        v-if="selectedMedium"
        class="tw:overflow-auto tw:p-4"
      >
        <div class="tw:font-bold">
          {{
            getLocaleText("stream_filters_media.filter_coverage", {
              medium: selectedMedium.label,
            })
          }}
        </div>
      </div>
      <BaseSeparator />
      <div :class="{ 'tw:flex-1 tw:overflow-auto': !isMobile }">
        <template v-if="showInternationalPlus">
          <div class="tw:p-4">
            <Checkbox
              v-model="localInternationalPlus"
              :color="selectedMedium.field"
              data-testid="international-plus"
              dense
              :label="
                getLocaleText('stream_filters_media.include_international_plus')
              "
              new-design
            />
          </div>
        </template>
        <template v-if="showMentionTypes">
          <StreamFiltersMediaMentionTypes
            v-model:selected-social-content="selectedSocialContent"
            v-model:selected-tv-content="selectedTvContent"
            data-testid="mention-types"
            :medium="selectedMedium.field"
            :stream="stream"
          />
          <BaseSeparator />
        </template>
        <template v-if="showSocialFilters">
          <AccountTypeRadioSelector
            v-model="localsocialOnlyVerified"
            data-testid="social-account-selector"
            :medium="selectedMedium.field"
          />
          <BaseSeparator />
        </template>
        <template v-if="selectedMedium.field !== MediumField.SOCIAL">
          <StreamFiltersMediaLocations
            v-model:location-keys="locationKeys"
            :medium="selectedMedium.field"
          />
          <BaseSeparator />
        </template>
        <StreamFiltersMediaCategories
          v-model:category-keys="categoryKeys"
          :medium="selectedMedium.field"
        />
        <template
          v-if="!isAdminMode && selectedMedium.field === MediumField.ONLINE"
        >
          <BaseSeparator />
          <StreamFiltersMediaDomainAuthority
            v-model:domain-authority="domainAuthority"
          />
        </template>
        <template v-if="showTierSelector">
          <BaseSeparator />
          <TierRadioSelector
            v-model="localTierValue"
            data-testid="tier-selector"
            :medium="selectedMedium.field"
          />
        </template>
        <template v-if="showCaldSelector">
          <BaseSeparator />
          <CaldRadioSelector
            v-model="localCald"
            data-testid="cald"
            :is-admin="isAdminMode"
            :medium="selectedMedium.field"
          />
        </template>
        <template v-if="showSocialFilters">
          <BaseSeparator />
          <StreamFiltersMinMaxFilter
            v-model="socialImpactThreshold"
            v-model:enabled="socialImpactThresholdEnabled"
            :clamp="[0, 100]"
            class="tw:p-4"
            data-testid="social-impact-threshold"
            :max-placeholder="
              getLocaleText(
                'stream_filters_media.impact_threshold_max_placeholder'
              )
            "
            :min-placeholder="
              getLocaleText(
                'stream_filters_media.impact_threshold_min_placeholder'
              )
            "
          >
            <template #heading>
              {{ getLocaleText("stream_filters_media.impact_threshold") }}
            </template>
          </StreamFiltersMinMaxFilter>
        </template>
      </div>
      <template v-if="selectedMedium.field !== MediumField.SOCIAL">
        <BaseSeparator />
        <div class="tw:p-4">
          <BaseButton
            class="tw:w-full"
            color="secondary"
            data-testid="apply-to-all"
            :disabled="!canApplyToAll"
            :icon="hasAppliedToAll ? 'check' : ''"
            :label="getLocaleText('stream_filters_media.apply_all')"
            new-design
            @click="applyToAll"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseSeparator } from "shared/components/base";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import AccountTypeRadioSelector from "shared/components/selectors/radio/AccountTypeRadioSelector.vue";
import CaldRadioSelector from "shared/components/selectors/radio/CaldRadioSelector.vue";
import TierRadioSelector from "shared/components/selectors/radio/TierRadioSelector.vue";
import {
  type Medium,
  type MediumName,
  getSources,
  MediumField,
} from "shared/helpers/media";
import type { StreamFiltersMinMax } from "shared/helpers/StreamFilters";
import features from "shared/services/features";
import type { Nullable, Stream } from "shared/types";
import {
  type MentionType,
  type SocialMentionType,
} from "shared/types/mentions";

import StreamFiltersMediaCategories from "./StreamFiltersMediaCategories.vue";
import StreamFiltersMediaDomainAuthority from "./StreamFiltersMediaDomainAuthority.vue";
import StreamFiltersMediaLocations from "./StreamFiltersMediaLocations.vue";
import StreamFiltersMediaMentionTypes from "./StreamFiltersMediaMentionTypes.vue";
import StreamFiltersMediaSelector from "./StreamFiltersMediaSelector.vue";
import StreamFiltersMinMaxFilter from "./StreamFiltersMinMax.vue";

interface StreamFiltersMediaProps {
  stream: Stream;
}

export type StreamTiers = {
  [key in `${MediumField}Tier`]?: boolean;
};

const isMobile = inject<boolean>("isMobile");
const isAdminMode = inject<boolean>("isAdminMode");

const tiers = defineModel<Nullable<StreamTiers>>("tiers");

const includeInternationalPlus = defineModel<Nullable<boolean>>(
  "includeInternationalPlus",
  { required: true }
);

const socialOnlyVerified = defineModel<Nullable<boolean>>(
  "socialOnlyVerified",
  { default: null }
);

const socialImpactThreshold = defineModel<StreamFiltersMinMax>(
  "socialImpactThreshold",
  {
    required: true,
  }
);

const socialImpactThresholdEnabled = ref<boolean>(
  Object.values(socialImpactThreshold.value).some((range) => range !== null)
);

const includeOnlineCaldSources = defineModel<string>(
  "includeOnlineCaldSources",
  { default: "" }
);

const includePrintCaldSources = defineModel<string>("includePrintCaldSources", {
  default: "",
});

const media = defineModel<MediumName[]>("media", {
  default: () => [],
});

const refreshMedia = defineModel<boolean>("refreshMedia");

const locationKeys = defineModel<string[]>("locationKeys", {
  default: () => [],
});

const categoryKeys = defineModel<string[]>("categoryKeys", {
  default: () => [],
});

const domainAuthority = defineModel<StreamFiltersMinMax[]>("domainAuthority", {
  default: () => [],
});

const selectedTvContent = defineModel<MentionType[]>("selectedTvContent", {
  default: () => [],
});

const selectedSocialContent = defineModel<SocialMentionType[]>(
  "selectedSocialContent",
  {
    default: () => [],
  }
);

const props = defineProps<StreamFiltersMediaProps>();

const selectedMedium = ref<Nullable<Medium>>(null);

const showInternationalPlus = computed(
  () =>
    !isAdminMode &&
    selectedMedium.value?.field === MediumField.ONLINE &&
    features.has("has_international_plus")
);

const localInternationalPlus = computed<boolean>({
  get() {
    if (includeInternationalPlus.value !== null) {
      return includeInternationalPlus.value;
    }

    return Boolean(props.stream.international_plus);
  },
  set(value) {
    includeInternationalPlus.value = value;
  },
});

const localsocialOnlyVerified = computed<boolean>({
  get() {
    if (socialOnlyVerified.value !== null) {
      return socialOnlyVerified.value;
    }

    return props.stream.social_only_verified;
  },
  set(value) {
    socialOnlyVerified.value = value;
  },
});

const showCaldSelector = computed(
  () =>
    features.has("has_cald_sources") &&
    selectedMedium.value?.field &&
    [MediumField.ONLINE, MediumField.PRINT].includes(
      selectedMedium.value?.field
    )
);

const localCald = computed<string>({
  get() {
    if (selectedMedium.value?.field === MediumField.ONLINE) {
      return includeOnlineCaldSources.value;
    }

    if (selectedMedium.value?.field === MediumField.PRINT) {
      return includePrintCaldSources.value;
    }

    return "";
  },
  set(value) {
    if (selectedMedium.value?.field === MediumField.ONLINE) {
      includeOnlineCaldSources.value = value;
    }

    if (selectedMedium.value?.field === MediumField.PRINT) {
      includePrintCaldSources.value = value;
    }
  },
});

const enabledMedia = computed<Medium[]>(() =>
  getSources().filter(({ medium }) =>
    props.stream.enabled_media.includes(medium)
  )
);

const showMentionTypes = computed<boolean>(() => {
  if (!selectedMedium.value || isAdminMode) {
    return false;
  }

  const hasFeaturesEnabled =
    selectedMedium.value.relatedTypes.filter((relatedType) =>
      features.has(relatedType.featureFlag)
    ).length > 1;

  return (
    hasFeaturesEnabled &&
    Boolean(
      selectedMedium.value &&
        [MediumField.TV, MediumField.SOCIAL].includes(
          selectedMedium.value.field
        )
    )
  );
});

const showSocialFilters = computed<boolean>(() =>
  Boolean(
    selectedMedium.value &&
      [MediumField.SOCIAL].includes(selectedMedium.value.field)
  )
);

const localTierValue = computed({
  get() {
    if (!selectedMedium.value || !tiers.value) return false;

    return tiers.value[`${selectedMedium.value.field}Tier`];
  },
  set(value) {
    if (!selectedMedium.value) return;

    tiers.value = {
      ...tiers.value,
      [`${selectedMedium.value.field}Tier`]: value,
    };
  },
});

const showTierSelector = computed<boolean>(() =>
  Boolean(
    isAdminMode &&
      selectedMedium.value &&
      selectedMedium.value.field !== MediumField.SOCIAL
  )
);

function toggleMedium(medium: Medium, value: MediumName[]) {
  const mediumSelected = value.includes(medium.medium);

  if (mediumSelected) {
    selectedMedium.value = medium;
  } else {
    locationKeys.value = locationKeys.value.filter(
      (locationKey) => !locationKey.includes(medium.field)
    );

    categoryKeys.value = categoryKeys.value.filter(
      (locationKey) => !locationKey.includes(medium.field)
    );

    if (medium.field === MediumField.ONLINE) {
      includePrintCaldSources.value = "";
      domainAuthority.value = [];
      includeInternationalPlus.value = null;
    }

    if (medium.field === MediumField.PRINT) {
      includeOnlineCaldSources.value = "";
    }

    if (medium.field === MediumField.TV) {
      selectedTvContent.value = [];
    }

    if (medium.field === MediumField.SOCIAL) {
      selectedSocialContent.value = [];
      socialImpactThreshold.value = { min: null, max: null };
      socialOnlyVerified.value = null;
    }
  }
}

const canApplyToAll = ref<boolean>(true);
const hasAppliedToAll = ref<boolean>(false);

function applyToAll(): void {
  const locationIds = locationKeys.value
    .filter((locationKey) =>
      locationKey.includes(selectedMedium.value?.field || "")
    )
    .map((locationKey) => {
      const [, id] = locationKey.split("_");

      return id;
    });

  const categoryIds = categoryKeys.value
    .filter((categoryKey) =>
      categoryKey.includes(selectedMedium.value?.field || "")
    )
    .map((categoryKey) => {
      const [, id] = categoryKey.split("_");

      return id;
    });

  const newLocationKeys: string[] = [];

  const newCategoryKeys: string[] = categoryKeys.value.filter((locationKey) =>
    locationKey.includes(MediumField.SOCIAL)
  );

  enabledMedia.value
    .filter((medium) => medium.field !== MediumField.SOCIAL)
    .forEach((medium) => {
      locationIds.forEach((id) => {
        newLocationKeys.push(`${medium.field}_${id}`);
      });

      categoryIds.forEach((id) => {
        newCategoryKeys.push(`${medium.field}_${id}`);
      });
    });

  locationKeys.value = newLocationKeys;
  categoryKeys.value = newCategoryKeys;

  if (selectedMedium.value?.field === MediumField.ONLINE) {
    includePrintCaldSources.value = includeOnlineCaldSources.value;
  }

  if (selectedMedium.value?.field === MediumField.PRINT) {
    includeOnlineCaldSources.value = includePrintCaldSources.value;
  }

  nextTick(() => {
    canApplyToAll.value = false;
    hasAppliedToAll.value = true;
  });
}

watch(selectedMedium, () => {
  canApplyToAll.value = true;
  hasAppliedToAll.value = false;
});

watch(
  [
    locationKeys,
    categoryKeys,
    includeOnlineCaldSources,
    includePrintCaldSources,
  ],
  () => {
    canApplyToAll.value = true;
    hasAppliedToAll.value = false;
  }
);
</script>
